import { useState, useEffect, lazy, Suspense } from "react";
import { formatDate } from "../utils/dates";
import { wellPhaseLabel } from "../utils/wellPhase";
import axios from "axios";
import ErrorBanner from "../components/ErrorBanner";
import { LoadingLine, PageFallback } from "../components/Spinner";
import { useTranslation } from "react-i18next";
import { weatherLang } from "../utils/weather";
import { jobTypeLabel } from "../utils/jsaLabels";

const RigStateEngine = lazy(() => import("./RigStateEngine"));

// Stored value → dashboard.main.rigTypes.* key (same labels as the
// Dashboard's register-rig form).
const RIG_TYPES = [
  ["Workover Unit", "workoverUnit"],
  ["Drilling Rig", "drillingRig"],
  ["Completion Unit", "completionUnit"],
  ["Pump Truck", "pumpTruck"],
  ["Coil Tubing Unit", "coilTubingUnit"],
];
const typeKey = (v) => (RIG_TYPES.find(([value]) => value === v) || [])[1];

const RIG_STATUSES = [
  "Active",
  "Standby",
  "Moving",
  "Down for Maintenance",
  "Released",
];

export default function RigDetail({ rig, token, wells, user, onBack, onUpdated, hideAssignWell = false }) {
  const { t, i18n } = useTranslation();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showRigState, setShowRigState] = useState(false);
  const [form, setForm] = useState({
    name: rig.name,
    type: rig.type,
    contractor: rig.contractor,
    status: rig.status,
    rigNumber: rig.rigNumber || "",
    area: rig.area || "",
    hourlyRate: rig.hourlyRate ?? "",
    dayRate: rig.dayRate ?? "",
  });
  const dateLocale = i18n.language === "es" ? "es-US" : "en-US";
  const fmtDay = (d) => formatDate(d, i18n.language);
  // What the rig is doing — from its tour sheets and jobs (server:
  // utils/rigActivity.js), the same everywhere. Absent for rigs that aren't
  // tracked that way: the rig record is used as before.
  const act = live?.rig?.activity || rig.activity || null;
  const tracked = !!act && (act.wellHistory.length > 0 || !!act.todayReport || !!act.job);
  const ACT_COLOR = { working: "#00cc66", idle: "rgba(255,255,255,0.6)", down: "#ff4466" };
  const historyRows = tracked
    ? act.wellHistory.map((h) => ({ wellName: h.wellName, customerName: h.customerName, assignedAt: h.from, releasedAt: h.to }))
    : [...rig.history].reverse();
  const shownWellName = tracked ? act.currentWell?.wellName || "" : rig.currentWellName;
  // Delete rig lives in Edit rig, behind typing the rig's name (or number).
  const [confirmText, setConfirmText] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteMatches = [rig.name, rig.rigNumber].filter(Boolean).some((v) => String(v).trim().toLowerCase() === confirmText.trim().toLowerCase());
  async function deleteThisRig() {
    if (!deleteMatches) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/rigs/${rig._id}`, { headers: { Authorization: "Bearer " + token } });
      onUpdated();
    } catch (err) {
      window.alert(err.response?.data?.error || t("rigDetail.deleteFailed"));
    }
  }
  const typeLabel = (v) => (typeKey(v) ? t(`dashboard.main.rigTypes.${typeKey(v)}`) : v);
  const statusLabel = (v) => t(`dashboard.main.rigStatus.${v}`, { defaultValue: String(v || "").toUpperCase() });
  const [selectedWellId, setSelectedWellId] = useState(rig.currentWellId || "");
  const [activeTab, setActiveTab] = useState("live");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);

  const headers = {
    Authorization: "Bearer " + token,
    "Content-Type": "application/json",
  };
  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";

  const statusColors = {
    Active: "#00f5ff",
    Standby: "#ffaa00",
    Moving: "#0080ff",
    "Down for Maintenance": "#ff4466",
    Released: "rgba(255,255,255,0.3)",
  };

  const lang = weatherLang(i18n);
  useEffect(() => {
    fetchLive();
    const interval = setInterval(fetchLive, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line
  }, [rig._id, lang]);

  async function fetchLive() {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/rigs/${rig._id}/live`,
        { headers, params: { lang } },
      );
      setLive(res.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("rigDetail.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/rigs/${rig._id}`, form, {
        headers,
      });
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || t("rigDetail.updateFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function handleAssign() {
    setAssignSaving(true);
    setError("");
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/rigs/${rig._id}/assign`,
        { wellId: selectedWellId || null },
        { headers },
      );
      setAssigning(false);
      fetchLive();
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || t("rigDetail.assignFailed"));
    } finally {
      setAssignSaving(false);
    }
  }

  const tabs = ["live", "history"];

  if (showRigState)
    return (
      <Suspense fallback={<PageFallback />}>
        <RigStateEngine
          rig={rig}
          token={token}
          onBack={() => setShowRigState(false)}
        />
      </Suspense>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* NAV */}
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: `1px solid ${border}`,
          padding: "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: "22px",
            fontWeight: "900",
            letterSpacing: "0.3px",
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          RIGIQ
        </div>
        <button
          onClick={onBack}
          style={{
            color: "rgba(0,245,255,0.5)",
            background: "none",
            border: `1px solid ${border}`,
            cursor: "pointer",
            fontSize: "11px",
            padding: "5px 12px",
            borderRadius: "4px",
            letterSpacing: "1px",
          }}
        >
          {t("rigDetail.back")}
        </button>
      </nav>

      <main style={{ padding: "28px" }}>
        {/* RIG HEADER */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "12px",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          {!editing ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "800",
                    color: "white",
                    marginBottom: "4px",
                    letterSpacing: "0.3px",
                  }}
                >
                  {rig.name}
                </h2>
                <p
                  style={{
                    color: "rgba(0,245,255,0.5)",
                    fontSize: "11px",
                    letterSpacing: "0.3px",
                    marginBottom: "16px",
                  }}
                >
                  {rig.rigNumber ? `#${rig.rigNumber} · ` : ""}
                  {[rig.contractor, typeLabel(rig.type)].filter(Boolean).join(" · ")}
                </p>
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  {tracked ? (
                    <span
                      data-testid="rig-status"
                      style={{ backgroundColor: `${ACT_COLOR[act.status]}22`, color: ACT_COLOR[act.status], fontSize: "12px", fontWeight: "700", padding: "4px 12px", borderRadius: "20px", border: `1px solid ${ACT_COLOR[act.status]}` }}
                    >
                      ● {t(`serviceDash.state.${act.status}`)}
                    </span>
                  ) : (
                  <span
                    data-testid="rig-status"
                    style={{
                      backgroundColor: statusColors[rig.status] + "22",
                      color: statusColors[rig.status],
                      fontSize: "11px",
                      fontWeight: "700",
                      padding: "4px 12px",
                      borderRadius: "20px",
                      border: `1px solid ${statusColors[rig.status]}`,
                      letterSpacing: "1px",
                    }}
                  >
                    ● {statusLabel(rig.status)}
                  </span>
                  )}
                  {shownWellName && (
                    <span
                      data-testid="rig-current-well"
                      style={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: "12px",
                      }}
                    >
                      📍 {shownWellName}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <button
                  onClick={() => setShowRigState(true)}
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    padding: "6px 14px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    letterSpacing: "1px",
                  }}
                >
                  {t("rigDetail.rigState")}
                </button>
                {!hideAssignWell && (
                <button
                  onClick={() => setAssigning(!assigning)}
                  style={{
                    background: "none",
                    border: `1px solid ${border}`,
                    color: "rgba(0,245,255,0.6)",
                    fontSize: "11px",
                    padding: "6px 14px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    letterSpacing: "1px",
                  }}
                >
                  {assigning ? t("rigDetail.cancel") : t("rigDetail.assignWell")}
                </button>
                )}
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    background: "none",
                    border: `1px solid ${border}`,
                    color: "rgba(0,245,255,0.6)",
                    fontSize: "11px",
                    padding: "6px 14px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    letterSpacing: "1px",
                  }}
                >
                  {t("rigDetail.editRig")}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                  marginBottom: "12px",
                }}
              >
                {[
                  { label: t("rigDetail.fields.name"), field: "name" },
                  { label: t("rigDetail.fields.contractor"), field: "contractor" },
                  { label: t("rigDetail.fields.rigNumber"), field: "rigNumber" },
                  { label: t("rigDetail.fields.area"), field: "area" },
                  { label: t("rigDetail.fields.hourlyRate"), field: "hourlyRate", type: "number" },
                  { label: t("rigDetail.fields.dayRate"), field: "dayRate", type: "number" },
                ].map(({ label, field, type }) => (
                  <div key={field}>
                    <label
                      style={{
                        color: cyan,
                        fontSize: "10px",
                        letterSpacing: "0.3px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {label}
                    </label>
                    <input
                      type={type || "text"}
                      min={type ? "0" : undefined}
                      inputMode={type ? "decimal" : undefined}
                      value={form[field]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [field]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        backgroundColor: "rgba(0,245,255,0.05)",
                        border: `1px solid rgba(0,245,255,0.2)`,
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: "white",
                        fontSize: "13px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                ))}
                <div>
                  <label
                    style={{
                      color: cyan,
                      fontSize: "10px",
                      letterSpacing: "0.3px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    {t("rigDetail.fields.type")}
                  </label>
                  <select
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, type: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "#020817",
                      border: `1px solid rgba(0,245,255,0.2)`,
                      borderRadius: "6px",
                      padding: "8px 12px",
                      color: "white",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  >
                    {RIG_TYPES.map(([value, key]) => (
                      <option key={value} value={value}>
                        {t(`dashboard.main.rigTypes.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      color: cyan,
                      fontSize: "10px",
                      letterSpacing: "0.3px",
                      display: "block",
                      marginBottom: "4px",
                    }}
                  >
                    {t("rigDetail.fields.status")}
                  </label>
                  <select
                    value={form.status}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, status: e.target.value }))
                    }
                    style={{
                      width: "100%",
                      backgroundColor: "#020817",
                      border: `1px solid rgba(0,245,255,0.2)`,
                      borderRadius: "6px",
                      padding: "8px 12px",
                      color: "white",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  >
                    {RIG_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {statusLabel(s)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    background: saving
                      ? "rgba(0,245,255,0.1)"
                      : "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: saving ? "rgba(0,245,255,0.5)" : "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    padding: "8px 20px",
                    borderRadius: "4px",
                    border: saving ? `1px solid ${border}` : "none",
                    cursor: saving ? "not-allowed" : "pointer",
                    letterSpacing: "1px",
                  }}
                >
                  {saving ? t("rigDetail.saving") : t("rigDetail.save")}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  style={{
                    background: "none",
                    border: `1px solid ${border}`,
                    color: "rgba(0,245,255,0.5)",
                    fontSize: "11px",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {t("rigDetail.cancel")}
                </button>
              </div>
              {/* Delete rig (admin): type the rig's name or number to confirm */}
              {user?.role === "admin" && (
                <div data-testid="rig-delete-zone" style={{ marginTop: "18px", borderTop: "1px solid rgba(255,68,102,0.25)", paddingTop: "12px" }}>
                  {!confirmingDelete ? (
                    <button type="button" data-testid="rig-delete" onClick={() => setConfirmingDelete(true)} style={{ background: "none", border: "1px solid rgba(255,68,102,0.5)", color: "#ff6680", fontSize: "13px", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }}>
                      {t("rigDetail.delete")}
                    </button>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", maxWidth: "420px" }}>
                      <label htmlFor="rig-delete-confirm" style={{ color: "#ff8899", fontSize: "14px" }}>
                        {t("rigDetail.typeToDelete", { name: rig.rigNumber ? `${rig.name} (${rig.rigNumber})` : rig.name })}
                      </label>
                      <input id="rig-delete-confirm" data-testid="rig-delete-confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,68,102,0.5)", color: "white", borderRadius: "6px", padding: "8px 10px", fontSize: "15px" }} />
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button type="button" data-testid="rig-delete-go" disabled={!deleteMatches} onClick={deleteThisRig} style={{ background: deleteMatches ? "#ff4466" : "rgba(255,68,102,0.2)", border: "none", color: "white", fontSize: "13px", fontWeight: 700, padding: "8px 14px", borderRadius: "6px", cursor: deleteMatches ? "pointer" : "not-allowed" }}>
                          {t("rigDetail.deleteForGood")}
                        </button>
                        <button type="button" onClick={() => { setConfirmingDelete(false); setConfirmText(""); }} style={{ background: "none", border: `1px solid ${border}`, color: "rgba(255,255,255,0.7)", fontSize: "13px", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" }}>
                          {t("rigDetail.cancel")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ASSIGN WELL */}
          {assigning && (
            <div
              style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: `1px solid ${border}`,
              }}
            >
              <label
                style={{
                  color: cyan,
                  fontSize: "10px",
                  letterSpacing: "0.3px",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                {t("rigDetail.assignToWell")}
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <select
                  value={selectedWellId}
                  onChange={(e) => setSelectedWellId(e.target.value)}
                  style={{
                    flex: 1,
                    backgroundColor: "#020817",
                    border: `1px solid rgba(0,245,255,0.2)`,
                    borderRadius: "6px",
                    padding: "8px 12px",
                    color: "white",
                    fontSize: "13px",
                    outline: "none",
                  }}
                >
                  <option value="">{t("rigDetail.releaseWell")}</option>
                  {wells.map((w) => (
                    <option key={w.id || w._id} value={w.id || w._id}>
                      {w.well_name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssign}
                  disabled={assignSaving}
                  style={{
                    background: assignSaving
                      ? "rgba(0,245,255,0.1)"
                      : "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: assignSaving ? "rgba(0,245,255,0.5)" : "#020817",
                    fontWeight: "700",
                    fontSize: "11px",
                    padding: "8px 20px",
                    borderRadius: "4px",
                    border: assignSaving ? `1px solid ${border}` : "none",
                    cursor: assignSaving ? "not-allowed" : "pointer",
                    letterSpacing: "1px",
                  }}
                >
                  {assignSaving ? t("rigDetail.saving") : t("rigDetail.confirm")}
                </button>
              </div>
            </div>
          )}
        </div>

        {error && <ErrorBanner message={error} />}

        {/* TABS */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            marginBottom: "24px",
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "4px",
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: "8px",
                background:
                  activeTab === tab
                    ? "linear-gradient(135deg, #00f5ff, #0080ff)"
                    : "none",
                color: activeTab === tab ? "#020817" : "rgba(0,245,255,0.5)",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: "700",
                letterSpacing: "0.3px",
              }}
            >
              {tab === "live" ? t("rigDetail.tabLive") : t("rigDetail.tabHistory")}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ marginBottom: "16px" }}>
            <LoadingLine label={t("rigDetail.loadingLive")} />
          </div>
        )}

        {/* LIVE TAB */}
        {!loading && activeTab === "live" && live && (
          <div>
            {!live.well ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px",
                  color: "rgba(0,245,255,0.3)",
                  fontSize: "13px",
                  letterSpacing: "0.3px",
                  border: `1px solid ${border}`,
                  borderRadius: "12px",
                }}
              >
                {t("rigDetail.notAssigned")}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {/* CURRENT WELL */}
                <div
                  style={{
                    backgroundColor: card,
                    border: `1px solid ${border}`,
                    borderRadius: "10px",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      color: cyan,
                      fontSize: "10px",
                      fontWeight: "700",
                      letterSpacing: "0.3px",
                      marginBottom: "12px",
                    }}
                  >
                    {t("rigDetail.currentWell")}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                      gap: "10px",
                    }}
                  >
                    {[
                      { label: t("rigDetail.wellName"), value: live.well.well_name },
                      { label: t("rigDetail.basin"), value: live.well.basin },
                      { label: t("rigDetail.location"), value: live.well.location },
                      {
                        label: t("rigDetail.phase"),
                        // Rig services: the job's type (Workover, Drillout …), not the well's phase.
                        value: tracked && act.job?.jobType ? t(`jobs.types.${act.job.jobType}`, { defaultValue: act.job.jobType }) : wellPhaseLabel(t, live.well),
                      },
                    ]
                      .filter((s) => s.value !== undefined && s.value !== null && String(s.value).trim() !== "")
                      .map((s) => (
                      <div
                        key={s.label}
                        style={{
                          backgroundColor: "rgba(0,245,255,0.05)",
                          borderRadius: "6px",
                          padding: "10px",
                        }}
                      >
                        <div
                          style={{
                            color: "rgba(0,245,255,0.4)",
                            fontSize: "9px",
                            letterSpacing: "0.3px",
                            marginBottom: "4px",
                          }}
                        >
                          {s.label}
                        </div>
                        <div
                          style={{
                            color: "white",
                            fontSize: "13px",
                            fontWeight: "700",
                          }}
                        >
                          {s.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* LIVE WEATHER */}
                {live.weather && (
                  <div
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        color: cyan,
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "0.3px",
                        marginBottom: "12px",
                      }}
                    >
                      {t("weather.liveWeather")}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: "10px",
                      }}
                    >
                      {[
                        {
                          label: t("weather.temperatureLabel"),
                          value: `${Math.round(live.weather.temperature)}°F`,
                        },
                        { label: t("weather.conditionsLabel"), value: live.weather.conditions },
                        {
                          label: t("weather.windLabel"),
                          value:
                            t("weather.wind", { speed: Math.round(live.weather.windSpeed) }) +
                            (live.weather.windGust ? t("weather.gusts", { speed: Math.round(live.weather.windGust) }) : ""),
                        },
                        {
                          label: t("weather.humidityLabel"),
                          value: `${live.weather.humidity}%`,
                        },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            backgroundColor: "rgba(0,245,255,0.05)",
                            borderRadius: "6px",
                            padding: "10px",
                          }}
                        >
                          <div
                            style={{
                              color: "rgba(0,245,255,0.4)",
                              fontSize: "9px",
                              letterSpacing: "0.3px",
                              marginBottom: "4px",
                            }}
                          >
                            {s.label}
                          </div>
                          <div
                            style={{
                              color: "white",
                              fontSize: "13px",
                              fontWeight: "700",
                            }}
                          >
                            {s.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* LATEST JSA */}
                {live.jsa && (
                  <div
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          color: cyan,
                          fontSize: "10px",
                          fontWeight: "700",
                          letterSpacing: "0.3px",
                        }}
                      >
                        {t("jsa.latest")}
                      </div>
                      {live.jsa.locked && (
                        <span
                          style={{
                            color: "#ffaa00",
                            fontSize: "10px",
                            letterSpacing: "1px",
                          }}
                        >
                          {t("jsa.locked")}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                        gap: "10px",
                      }}
                    >
                      {[
                        { label: t("jsa.jobType"), value: live.jsa.jobType ? jobTypeLabel(t, live.jsa.jobType) : t("jsa.na") },
                        { label: t("jsa.steps"), value: live.jsa.steps?.length || 0 },
                        {
                          label: t("jsa.signatures"),
                          value:
                            live.jsa.signatures?.filter((s) => s.name).length ||
                            0,
                        },
                        {
                          label: t("jsa.h2s"),
                          value: live.jsa.h2sPresent ? t("jsa.h2sYes") : t("jsa.h2sNo"),
                        },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            backgroundColor: "rgba(0,245,255,0.05)",
                            borderRadius: "6px",
                            padding: "10px",
                          }}
                        >
                          <div
                            style={{
                              color: "rgba(0,245,255,0.4)",
                              fontSize: "9px",
                              letterSpacing: "0.3px",
                              marginBottom: "4px",
                            }}
                          >
                            {s.label}
                          </div>
                          <div
                            style={{
                              color: "white",
                              fontSize: "13px",
                              fontWeight: "700",
                            }}
                          >
                            {s.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* LATEST REPORT */}
                {live.reports?.length > 0 && (
                  <div
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        color: cyan,
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "0.3px",
                        marginBottom: "12px",
                      }}
                    >
                      {t("rigDetail.latestReport")}
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.6)",
                        fontSize: "13px",
                        lineHeight: "1.8",
                        marginBottom: "12px",
                      }}
                    >
                      {live.reports[0].operations}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "20px",
                        fontSize: "11px",
                        color: "rgba(0,245,255,0.6)",
                        letterSpacing: "1px",
                      }}
                    >
                      <span>
                        📅{" "}
                        {fmtDay(live.reports[0].date)}
                      </span>
                      {live.reports[0].format === "tour_sheet" ? (
                        live.reports[0].totals && <span>{t("rigReport.cardHours.working", { h: live.reports[0].totals.working })}</span>
                      ) : (
                        <>
                          <span>{t("rigReport.daily.footage", { v: live.reports[0].footage })}</span>
                          <span>{t("rigReport.daily.mudWeight", { v: live.reports[0].mud_weight })}</span>
                          <span>{t("rigReport.daily.hoursDrilling", { v: live.reports[0].hours_drilling })}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* RECENT REPORTS */}
                {live.reports?.length > 1 && (
                  <div
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        color: cyan,
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "0.3px",
                        marginBottom: "12px",
                      }}
                    >
                      {t("rigDetail.recentReports")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                      }}
                    >
                      {live.reports.slice(1).map((r) => (
                        <div
                          key={r._id}
                          style={{
                            backgroundColor: "rgba(0,245,255,0.03)",
                            borderRadius: "6px",
                            padding: "12px",
                            border: `1px solid ${border}`,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <span
                              style={{
                                color: cyan,
                                fontSize: "11px",
                                fontWeight: "700",
                              }}
                            >
                              {fmtDay(r.date)}
                            </span>
                            <span
                              style={{
                                color: "rgba(255,255,255,0.3)",
                                fontSize: "10px",
                              }}
                            >
                              {r.format === "tour_sheet"
                                ? t("rigReport.cardHours.working", { h: r.totals?.working ?? 0 })
                                : t("rigReport.daily.hoursDrilling", { v: r.hours_drilling })}
                            </span>
                          </div>
                          <p
                            style={{
                              color: "rgba(255,255,255,0.5)",
                              fontSize: "12px",
                              lineHeight: "1.5",
                            }}
                          >
                            {r.operations?.slice(0, 120)}...
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* LATEST EXPENSE */}
                {live.expenses?.length > 0 && (
                  <div
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        color: cyan,
                        fontSize: "10px",
                        fontWeight: "700",
                        letterSpacing: "0.3px",
                        marginBottom: "12px",
                      }}
                    >
                      {t("rigDetail.latestCost")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "16px",
                          fontSize: "12px",
                          color: "rgba(0,245,255,0.6)",
                        }}
                      >
                        {live.expenses[0].rig_day_rate > 0 && (
                          <span>
                            🏗 ${live.expenses[0].rig_day_rate.toLocaleString()}
                          </span>
                        )}
                        {live.expenses[0].crew_labor > 0 && (
                          <span>
                            👷 ${live.expenses[0].crew_labor.toLocaleString()}
                          </span>
                        )}
                        {live.expenses[0].fuel_diesel > 0 && (
                          <span>
                            ⛽ ${live.expenses[0].fuel_diesel.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          color: cyan,
                          fontSize: "22px",
                          fontWeight: "800",
                        }}
                      >
                        $
                        {live.expenses[0].total.toLocaleString(dateLocale, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {!loading && activeTab === "history" && (
          <div>
            {historyRows.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "60px",
                  color: "rgba(0,245,255,0.3)",
                  fontSize: "13px",
                  letterSpacing: "0.3px",
                  border: `1px solid ${border}`,
                  borderRadius: "12px",
                }}
              >
                {t("rigDetail.noHistory")}
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {historyRows.map((h, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: card,
                      border: `1px solid ${border}`,
                      borderRadius: "10px",
                      padding: "16px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "white",
                          fontWeight: "700",
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        {h.wellName}
                        {h.customerName ? <span style={{ color: "rgba(255,255,255,0.5)", fontWeight: 400 }}> · {h.customerName}</span> : null}
                      </div>
                      <div
                        style={{
                          color: "rgba(0,245,255,0.5)",
                          fontSize: "11px",
                          letterSpacing: "1px",
                        }}
                      >
                        {t("rigDetail.assigned", { date: fmtDay(h.assignedAt) })}
                        {h.releasedAt && ` · ${t("rigDetail.released", { date: fmtDay(h.releasedAt) })}`}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {!h.releasedAt ? (
                        <span
                          style={{
                            color: "#00f5ff",
                            fontSize: "10px",
                            fontWeight: "700",
                            letterSpacing: "0.3px",
                            backgroundColor: "rgba(0,245,255,0.1)",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            border: "1px solid rgba(0,245,255,0.3)",
                          }}
                        >
                          {t("rigDetail.current")}
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "rgba(255,255,255,0.3)",
                            fontSize: "10px",
                            letterSpacing: "0.3px",
                          }}
                        >
                          {t("rigDetail.days", {
                            count: Math.round((new Date(h.releasedAt) - new Date(h.assignedAt)) / 86400000) + (tracked ? 1 : 0),
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
